import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaSearch, FaChevronUp, FaChevronDown, FaCheck, FaPencilAlt, FaTrash, FaGripVertical, FaSpinner } from 'react-icons/fa';
import { reportTypeService } from '../services';
import { CustomInstructionService } from '../services/CustomInstructionService';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../services/Debug';
import useNotification from '../components/Notifications/useNotifications';
import Notification from '../components/Notifications/Notification';
import { ReportTypeNames } from '../models/ReportTypeNames';
import Button from '../components/buttons/Button';
import Card from '../components/Card/Card';
import BackButton from '../components/buttons/BackButton';
import { useLocation } from 'react-router-dom';
import FullScreenTextEditor from '../components/editors/FullScreenTextEditor';
import { useFullScreenTextEditor } from '../hooks/useFullScreenTextEditor';
import HoverTooltip from '../components/tooltips/HoverTooltip';
import './ReportTypePanel.css';
import { isNative, isCapacitor } from '../utils/platform';
import SkeletonManager from '../components/loaders/SkeletonManager';
import ReportTypeCreatorOverlay from './ReportTypeCreatorOverlay';

// Inline component mirroring ReportTypesTab "click to open fullscreen" design
function ClickToEditBox({ title, placeholder, value, onChange, minHeight = 80, style = {} }) {
  const { editorProps, openEditor } = useFullScreenTextEditor({
    title,
    placeholder,
    onSave: async (v) => onChange(v),
    showCharCount: true,
  });

  return (
    <>
      <div
        role="button"
        onClick={() => openEditor(value || '')}
        onFocus={() => openEditor(value || '')}
        tabIndex={0}
        style={{
          minHeight: minHeight,
          padding: 10,
          border: '1px solid #e0e0e0',
          borderRadius: 8,
          background: '#fafafa',
          color: '#333',
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          cursor: 'text',
          ...style,
        }}
        title={value || ''}
      >
        {value || placeholder}
      </div>
      <FullScreenTextEditor {...editorProps} />
    </>
  );
}

function ExampleTextarea({ value, onChange, onRemove, canRemove, index }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <ClickToEditBox
        title={`Exemple ${index + 1}`}
        placeholder={`Exemple ${index + 1} (texte brut)...`}
        value={value}
        onChange={onChange}
        minHeight={80}
        style={{ flex: 1, width: '100%' }}
      />
      {canRemove && (
        <Button
          type="fixed"
          onClick={onRemove}
          title="Supprimer cet exemple"
          variant="danger"
          fontSize="small"
        >
          −
        </Button>
      )}
    </div>
  );
}

const customInstructionService = new CustomInstructionService();

function ReportTypePanel() {
  const log = createLogger('ReportTypePanel');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { profile } = useAuth();
  const location = useLocation();
  const backTo = location?.state?.from === 'recorder' ? '/recorder' : '/home';
  const { notification, showNotification, hideNotification } = useNotification();
  const [reportTypeModel, setReportTypeModel] = useState(null);
  const [originalFavorites, setOriginalFavorites] = useState([]); // Store original state (names)
  const [favorites, setFavorites] = useState([]); // names in order
  const [specialties, setSpecialties] = useState({}); // { cardiology: [...], radiology: [...], ... }
  const [userSpecialty, setUserSpecialty] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSpecialties, setExpandedSpecialties] = useState({}); // Track which specialties are expanded
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customInstructions, setCustomInstructions] = useState({});
  const [showInstructionEditor, setShowInstructionEditor] = useState(false);
  const [currentEditingType, setCurrentEditingType] = useState(null);
  const [instructionValue, setInstructionValue] = useState('');
  const [savingInstruction, setSavingInstruction] = useState(false);
  const [favoritesLoading, setFavoritesLoading] = useState(true); // Internal loading for favorites section
  // Creation state (from old ReportTypes.js)
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [examples, setExamples] = useState(['']);
  const [creating, setCreating] = useState(false);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragCounter = useRef(0);

  useEffect(() => {
   setIsInitialLoading(true);
    loadData();
  }, []);

  // Initialize expanded state when specialties are loaded
  useEffect(() => {
    if (Object.keys(specialties).length > 0 && Object.keys(expandedSpecialties).length === 0) {
      // Expand user's specialty by default, collapse others
      const initialExpanded = {};

      // Initialize special sections (personal and enterprise) as collapsed
      initialExpanded['__personal__'] = false;
      initialExpanded['__enterprise__'] = false;

      // Initialize specialty sections
      Object.keys(specialties).forEach(spec => {
        initialExpanded[spec] = spec === userSpecialty;
      });

      setExpandedSpecialties(initialExpanded);
    }
  }, [specialties, userSpecialty, expandedSpecialties]);

  // Helper function to filter report types by search term
  const filterTypesBySearch = (types) => {
    if (!searchTerm.trim()) return types;
    const lowerSearch = searchTerm.toLowerCase();
    return types.filter(type => {
      const displayName = ReportTypeNames.formatDisplayName(type.name).toLowerCase();
      const description = type.description?.toLowerCase() || '';
      return displayName.includes(lowerSearch) || description.includes(lowerSearch);
    });
  };

  // Get ordered specialty keys: user's specialty first, then general, then others alphabetically
  const getOrderedSpecialties = () => {
    const specialtyKeys = Object.keys(specialties);

    // If no user specialty is set, put general first, then others alphabetically
    if (!userSpecialty || !specialtyKeys.includes(userSpecialty)) {
      const withoutGeneral = specialtyKeys.filter(s => s.toLowerCase() !== 'general').sort();
      if (specialtyKeys.some(s => s.toLowerCase() === 'general')) {
        const general = specialtyKeys.find(s => s.toLowerCase() === 'general');
        return [general, ...withoutGeneral];
      }
      return specialtyKeys.sort();
    }

    // User specialty exists: user specialty → general → others alphabetically
    const withoutUserAndGeneral = specialtyKeys.filter(s =>
      s !== userSpecialty && s.toLowerCase() !== 'general'
    ).sort();

    const general = specialtyKeys.find(s => s.toLowerCase() === 'general');

    if (general && general !== userSpecialty) {
      return [userSpecialty, general, ...withoutUserAndGeneral];
    }

    return [userSpecialty, ...withoutUserAndGeneral];
  };

  const loadData = async () => {
    const minimumLoadingTime = 500;
    const startTime = Date.now();
    
    // Set internal favorites loading
    setFavoritesLoading(true);
    
    // Loading is already set in useEffect before calling this function
    try {
      // Fetch the report types data from API and custom instructions in parallel
      // Note: Using new unified /available endpoint - single API call for all report types
      const [reportTypeNames, customInstructionsData] = await Promise.all([
        reportTypeService.getNames(),
        customInstructionService.getAllCustomInstructions()
      ]);

      if (reportTypeNames) {
        setReportTypeModel(reportTypeNames);

        // Get favorites as names in order
        const favoriteNames = reportTypeNames.getFavorites();
        setFavorites(favoriteNames);
        setOriginalFavorites(favoriteNames);

        // Extract specialty grouping directly from the report types data
        const allTypes = reportTypeNames.getAll();
        const specialtyData = {};
        allTypes.forEach(type => {
          const specialty = type.specialty || 'general';
          if (!specialtyData[specialty]) {
            specialtyData[specialty] = [];
          }
          specialtyData[specialty].push({
            name: type.name,
            description: type.description
          });
        });

        setSpecialties(specialtyData);

        // Determine default specialty
        const userSpecialty = profile?.specialty?.toLowerCase();
        if (userSpecialty && specialtyData[userSpecialty]) {
          setUserSpecialty(userSpecialty);
        }
      }

      // Process custom instructions into a map
      const instructionsMap = {};
      customInstructionsData.forEach(instruction => {
        instructionsMap[instruction.reportTypeName] = {
          id: instruction.id,
          content: instruction.content
        };
      });
      setCustomInstructions(instructionsMap);
        
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } catch (error) {
      log.error('Failed to load data:', error);
      showNotification('Erreur lors du chargement des données', 'error');
      
       
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumLoadingTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
    } finally {
  
    
      setFavoritesLoading(false);
      setIsInitialLoading(false);
    }
  };

  // Gold/Enterprise gating for creation
    const isGoldOrEnterprise = React.useMemo(() => {
      return Boolean(
        profile?.isGoldOrEnterprise === true ||
        profile?.isgoldorenterprise === true ||
        profile?.subscription?.planType === 'gold' ||
        profile?.subscription?.planType === 'enterprise' ||
        profile?.subscription?.plan === 'gold' ||
        profile?.subscription?.plan === 'enterprise'
      );
    }, [profile]);
  const goldGatingMsg = 'Réservé aux abonnements Gold et Entreprise';

  const isPersonalType = (nameOrType) => {
    const name = typeof nameOrType === 'string' ? nameOrType : nameOrType?.name;
    if (!name || !reportTypeModel) return false;
    return reportTypeModel.getCategoryForType(name) === 'personal';
  };

  const handleDeleteType = async (nameOrType) => {
    const name = typeof nameOrType === 'string' ? nameOrType : nameOrType?.name;
    if (!name || !isPersonalType(name)) return;
    if (!window.confirm(`Supprimer le modèle personnel "${ReportTypeNames.formatDisplayName(name)}" ?`)) return;
    try {
   
      setFavoritesLoading(true); // Show loading in favorites section
      await reportTypeService.deleteReportType(name);
      await loadData();
      showNotification('Modèle supprimé', 'success');
    } catch (e) {
      log.error('Failed to delete report type', e);
      showNotification('Erreur lors de la suppression', 'error');
      setFavoritesLoading(false); // Clear loading on error
    } 
  };

  const handleToggleFavorite = (type) => {
    if (!reportTypeModel) return;
    const name = typeof type === 'string' ? type : type?.name;
    if (!name) return;
    
    const isFavorite = reportTypeModel.isFavorite(name);

    // Protection rules: always keep at least one "consultation" and always keep "texte"
    // if (isFavorite) {
    //   const lowerName = name.toLowerCase();
    //   if (lowerName.includes('live')) {
    //     showNotification('Le mode Live doit rester actif', 'warning');
    //     return;
    //   }
      
    //   // // Prevent removing the last consultation-type favorite
    //   // if (lowerName.includes('consultation')) {
    //   //   const consultationFavoritesCount = favorites.filter((favName) => favName.toLowerCase().includes('consultation')).length;
    //   //   if (consultationFavoritesCount <= 1) {
    //   //     showNotification('Au moins un modèle de consultation doit rester actif', 'warning');
    //   //     return;
    //   //   }
    //   // }
    // }
    
    // Toggle in model
    reportTypeModel.toggleFavorite(name);
    
    // Update local state using names in order
    const newFavorites = reportTypeModel.getFavorites();
    setFavorites(newFavorites);
    setHasChanges(true);
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    // Add a custom drag image or style
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
    setDraggedItem(null);
    setDragOverIndex(null);
    dragCounter.current = 0;
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragCounter.current++;
    if (draggedItem !== null && draggedItem !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverIndex(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    dragCounter.current = 0;
    
    if (draggedItem === null || draggedItem === dropIndex) return;
    
    if (!reportTypeModel) return;
    
    const newFavorites = [...favorites];
    const draggedType = newFavorites[draggedItem];
    
    // Remove dragged item
    newFavorites.splice(draggedItem, 1);
    
    // Insert at new position
    const insertIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex;
    newFavorites.splice(insertIndex, 0, draggedType);
    
    // Update model and state
    reportTypeModel.setFavorites(newFavorites);
    setFavorites(newFavorites);
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!reportTypeModel) return;
    
    setIsSaving(true);
    try {
      // Get the current favorites (names) with implicit order by index
      const favoritesData = reportTypeModel.getFavorites();
      
      // Use the new replaceFavorites method with proper DTO formatting
      await reportTypeService.replaceFavorites(favoritesData);
      
      // Update original state
      setOriginalFavorites(favoritesData);
      setHasChanges(false);
      showNotification('Modifications sauvegardées avec succès');
    } catch (error) {
      log.error('Failed to save changes:', error);
      showNotification('Erreur lors de la sauvegarde', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    if (!reportTypeModel) return;
    
    // Restore original favorites in model
    reportTypeModel.setFavorites(originalFavorites);
    
    // Update local state
    setFavorites([...originalFavorites]);
    setHasChanges(false);
    showNotification('Modifications annulées');
  };

  const saveInstruction = async (value) => {
    if (!currentEditingType) return;
    
    setSavingInstruction(true);
    setFavoritesLoading(true); // Show loading while saving instructions
    try {
      // Check if instruction already exists for this type
      const existingInstruction = customInstructions[currentEditingType];
      let deleted = false;
      
      if (value.trim() === '') {
        // If emptied, delete existing instruction if present; otherwise no-op
        if (existingInstruction?.id) {
          await customInstructionService.deleteCustomInstruction(existingInstruction.id);
          deleted = true;
        }
      } else if (existingInstruction?.id) {
        // Update existing instruction
        await customInstructionService.updateCustomInstruction(existingInstruction.id, {
          reportTypeName: currentEditingType,
          content: value
        });
      } else {
        // Create new instruction
        await customInstructionService.createCustomInstruction({
          reportTypeName: currentEditingType,
          content: value
        });
      }
      
      // Reload data to get updated instructions
      await loadData();
      
      // Close editor
      setShowInstructionEditor(false);
      setCurrentEditingType(null);
      setInstructionValue('');
      
      showNotification(deleted ? 'Instructions supprimées' : 'Instructions enregistrées avec succès');
    } catch (error) {
      log.error('Failed to save instruction:', error);
      showNotification('Erreur lors de l\'enregistrement des instructions', 'error');
      throw error; // Let the editor handle the error
    } finally {
      setSavingInstruction(false);
    }
  };

  // Creation helpers (from old ReportTypes.js)
  const validateName = (name) => {
    if (!name || name.length > 50) return false;
    return /^[a-zA-Z\s]+$/.test(name);
  };

  const startCreate = () => {
    setIsCreating(true);
    setNewName('');
    setNewDescription('');
    setExamples(['']);
  };

  const cancelCreate = () => {
    setIsCreating(false);
    setNewName('');
    setNewDescription('');
    setExamples(['']);
  };

  const addExample = () => {
    if (examples.length < 10) {
      setExamples([...examples, '']);
    }
  };

  const removeExample = (index) => {
    if (examples.length > 1) {
      const newExamples = examples.filter((_, i) => i !== index);
      setExamples(newExamples);
    }
  };

  const updateExample = (index, value) => {
    const newExamples = [...examples];
    newExamples[index] = value;
    setExamples(newExamples);
  };

  const saveCreate = async () => {
    if (!validateName(newName)) {
      showNotification('Le nom doit contenir uniquement des lettres (max 50).', 'error');
      return;
    }

    // Check if report type with this name already exists in current model
    const allTypes = Object.values(specialties).flat();
    const exists = reportTypeModel?.hasType?.(newName) || favorites.some(f => f.toLowerCase() === newName.toLowerCase()) || allTypes.some(t => t.name.toLowerCase() === newName.toLowerCase());
    if (exists) {
      showNotification(`Un type "${newName}" existe déjà.`, 'error');
      return;
    }

    const validExamples = examples.filter(example => example.trim() !== '');

    try {
      setCreating(true);
      const reportData = { name: newName, description: newDescription };
      if (validExamples.length > 0) {
        await reportTypeService.createReportTypeWithExamples({ ...reportData, examples: validExamples });
        showNotification(`Type créé avec ${validExamples.length} exemple(s). Le traitement peut prendre quelques minutes.`, 'success');
      } else {
        await reportTypeService.createReportType(reportData);
        showNotification('Type créé avec succès.', 'success');
      }
      await loadData();
      cancelCreate();
    } catch (err) {
      log.error('Failed to create report type', err);
      showNotification('Erreur lors de la création du modèle', 'error');
    } finally {
      setCreating(false);
    }
  };

  // Helper function to render category badge
  const renderCategoryBadge = (type) => {
    if (!reportTypeModel) return null;
    
    const badgeInfo = reportTypeModel.getCategoryBadgeInfo(type.name);
    if (!badgeInfo) return null;
    
    return (
      <span className={`category-badge ${badgeInfo.className}`}>
        {badgeInfo.text}
      </span>
    );
  };

  const toggleSpecialty = (specialty) => {
    setExpandedSpecialties(prev => ({
      ...prev,
      [specialty]: !prev[specialty]
    }));
  };

  if (isInitialLoading || favoritesLoading) {
    return (
      <SkeletonManager  active={true} fullWidth={true} />
    );
  }

  return (
    <div className={`report-type-panel ${isCapacitor ? 'is-capacitor' : ''}`}>
      <div className="panel-header">
        <div className="header-left">
          <BackButton to={backTo} />
        </div>
        <div className="header-actions">
          {/* Hide "Créer un modèle" on Capacitor with Silver plan */}
          {!(isCapacitor && profile?.subscription?.planType?.toLowerCase() === 'silver') && (
            <HoverTooltip text={!isGoldOrEnterprise ? goldGatingMsg : ''} disabled={isGoldOrEnterprise}>
              <span>
                <Button
                  variant="primary"
                  onClick={startCreate}
                  icon={FaPlus}
                  disabled={!isGoldOrEnterprise || isSaving}
                >
                  Créer un modèle
                </Button>
              </span>
            </HoverTooltip>
          )}
          {!hasChanges && (
            <Button
              variant="secondary"
              onClick={async () => {
                const specialty = profile?.specialty || 'general';
                if (!window.confirm(`Réinitialiser les favoris selon la spécialité "${specialty}" ?`)) return;
                try {
                  setFavoritesLoading(true); // Show loading in favorites section
                  await reportTypeService.resetFavorites(specialty);
                  await loadData();
                  showNotification('Favoris réinitialisés', 'success');
                } catch (e) {
                  log.error('Failed to reset favorites', e);
                  showNotification('Erreur lors de la réinitialisation', 'error');
                  setFavoritesLoading(false); // Clear loading on error
                } 
              }}
            >
              Réinitialiser
            </Button>
          )}
          {hasChanges && (
            <>
              <Button
                variant="secondary"
                onClick={handleCancelChanges}
                disabled={isSaving}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveChanges}
                disabled={isSaving}
              >
                {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </>
          )}
        </div>
      </div>

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          id={notification.id}
        />
      )}

      <div className="panel-content">
        {/* Favorites Section */}
        <div className="favorites-section">
          <h2><FaCheck className="reporttype-section-icon" /> Modèles actifs</h2>
          {!isCapacitor && <p className="panel-explanation" style={{ marginTop: 6, marginBottom: 10, color: '#666', fontSize: 14, lineHeight: '18px' }}>{isNative ? 'Toucher' : 'Cliquer'} pour enlever un modèle des favoris et glisser pour changer l'ordre des favoris.</p>}
          <div className="panel-explanation" style={{ marginTop: 6, marginBottom: 10, color: '#666', fontSize: 12, lineHeight: '18px' }}>
            <div><strong>Modèles actifs</strong> : vos favoris qui apparaissent en priorité (ex. dans le dictaphone).</div>
            <div><strong>Instructions personnalisées</strong> : utilisez l'icône crayon pour définir des consignes propres à chaque type.</div>
          </div>
          <div className="favorites-list">
            {favoritesLoading ? (
              <Card className="empty-state" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '120px' }}>
                <FaSpinner className="fa-spin" style={{ fontSize: '24px', marginBottom: '12px', color: '#666' }} />
                <p style={{ margin: 0, color: '#666' }}>Chargement des modèles actifs...</p>
              </Card>
            ) : favorites.length === 0 ? (
              <Card className="empty-state">
                <p>Aucun favori pour le moment</p>
                <p className="hint">Ajoutez des types de rapport depuis la liste de droite</p>
              </Card>
            ) : (
              favorites.map((type, index) => {
                const lowerName = String(type).toLowerCase();
                const isLiveForced = lowerName === 'live';
                const consultationFavoritesCount = favorites.filter((favName) => String(favName).toLowerCase().includes('consultation')).length;
                const isLastConsultationForced = lowerName.includes('consultation') && consultationFavoritesCount <= 1;
                const isForcedFavorite = isLastConsultationForced || isLiveForced;
                return (
                <div
                  key={`${type}-${index}`}
                  className={`report-type-item favorite-card ${!isCapacitor ? 'drag-enabled' : ''} ${dragOverIndex === index ? 'drag-over' : ''} ${isForcedFavorite ? 'forced-favorite' : ''}`}
                  onDragEnter={!isCapacitor ? (e) => handleDragEnter(e, index) : undefined}
                  onDragLeave={!isCapacitor ? handleDragLeave : undefined}
                  onDragOver={!isCapacitor ? handleDragOver : undefined}
                  onDrop={!isCapacitor ? (e) => handleDrop(e, index) : undefined}
                  onClick={(e) => {
                    // Only handle click if not clicking on action buttons
                    if (!e.target.closest('.report-type-instruction, .report-type-favorite-btn, .btn, .drag-handle')) {
                      handleToggleFavorite(type);
                    }
                  }}
                >
                  {!isCapacitor && (
                    <div
                      className="drag-handle"
                      draggable={!isCapacitor}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <FaGripVertical />
                    </div>
                  )}
                  <button 
                    className="report-type-button">
                    <span className={`report-type-icon ${ReportTypeNames.getIconClass(type)}`}>
                      {ReportTypeNames.getIconLetter(type)}
                    </span>
                    <div className="report-type-text">
                      <span className="report-type-name">
                        {ReportTypeNames.formatDisplayName(type)}
                      </span>
                      {reportTypeModel?.getTypeDetails?.(type)?.description && (
                        <span
                          className="report-type-description"
                          style={{ display: 'block', wordWrap: 'break-word', whiteSpace: 'normal' }}
                        >
                          {reportTypeModel.getTypeDetails(type)?.description}
                        </span>
                      )}
                    </div>
                  </button>
                  {!(reportTypeModel && reportTypeModel.isEnterprise(type)) && !(isCapacitor && !isGoldOrEnterprise) && (
                    <HoverTooltip
                      text={!isGoldOrEnterprise ? goldGatingMsg : (customInstructions[type] ? 'Modifier les instructions personnalisées' : 'Ajouter des instructions personnalisées')}
                      position="left"
                      arrow={true}
                      className="instruction-tooltip"
                    >
                      <button
                        className={`report-type-instruction ${customInstructions[type] ? 'has-instruction' : ''}`}
                        disabled={!isGoldOrEnterprise}
                        onClick={(e) => {
                          if (!isGoldOrEnterprise) return;
                          e.stopPropagation();
                          setCurrentEditingType(type);
                          setInstructionValue(customInstructions[type]?.content || '');
                          setShowInstructionEditor(true);
                        }}
                      >
                        <FaPencilAlt />
                      </button>
                    </HoverTooltip>
                  )}
                  {reportTypeModel && isPersonalType(type) && (
                    <HoverTooltip
                      text={'Supprimer ce type personnel'}
                      position="left"
                      arrow={true}
                    >
                      <Button
                        variant="danger"
                        type="inversed"
                        icon={FaTrash}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteType(type);
                        }}
                        title="Supprimer"
                      >
                      </Button>
                    </HoverTooltip>
                  )}
                  
                </div>
              )})
            )}
          </div>
        </div>

        {/* Available Types Section - Grouped by Specialty */}
        <div className="available-section">
          <h2>Modèles disponibles</h2>

          {/* Search Bar */}
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un modèle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Specialty Sections */}
          {Object.keys(specialties).length > 0 ? (
            <div className="specialty-sections">
              {/* Extract all types to categorize personal and enterprise */}
              {(() => {
                // Get ALL types from the main model, not just from specialty groupings
                // This ensures enterprise and personal types are included even if they're not in specialty groups
                const allTypes = reportTypeModel ? reportTypeModel.getAll() : [];
                

                const nonFavoriteAllTypes = allTypes.filter(type => !favorites.includes(type.name));
                

                // Personal types (user-created custom types)
                const personalTypes = nonFavoriteAllTypes.filter(type =>
                  reportTypeModel && isPersonalType(type.name)
                );
                
                const filteredPersonalTypes = filterTypesBySearch(personalTypes);

                // Enterprise types (organization-level types)
                const enterpriseTypes = nonFavoriteAllTypes.filter(type =>
                  reportTypeModel && reportTypeModel.isEnterprise(type.name)
                );
                
                const filteredEnterpriseTypes = filterTypesBySearch(enterpriseTypes);

                return (
                  <>
                    {/* Personal Types Section */}
                    {filteredPersonalTypes.length > 0 && (
                      <div className="specialty-section custom-types">
                        <button
                          className="specialty-header"
                          onClick={() => toggleSpecialty('__personal__')}
                        >
                          <div className="specialty-header-content">
                            <span className="specialty-name">
                              Modèles personnels
                              <span className="custom-badge">Personnel</span>
                            </span>
                            <span className="specialty-count">
                              {filteredPersonalTypes.length} modèle{filteredPersonalTypes.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          {expandedSpecialties['__personal__'] ? <FaChevronUp /> : <FaChevronDown />}
                        </button>

                        {expandedSpecialties['__personal__'] && (
                          <div className="specialty-types">
                            {filteredPersonalTypes.map((type) => {
                              const canDelete = reportTypeModel && isPersonalType(type.name);
                              return (
                                <div
                                  key={type.name}
                                  className="report-type-item"
                                  onClick={(e) => {
                                    if (!e.target.closest('.report-type-instruction, .report-type-favorite-btn, .btn')) {
                                      handleToggleFavorite(type);
                                    }
                                  }}
                                >
                                  <button className="report-type-button">
                                    <span className={`report-type-icon ${ReportTypeNames.getIconClass(type.name)}`}>
                                      {ReportTypeNames.getIconLetter(type.name)}
                                    </span>
                                    <div className="report-type-text">
                                      <div className="report-type-header">
                                        <span className="report-type-name">
                                          {ReportTypeNames.formatDisplayName(type.name)}
                                        </span>
                                        {renderCategoryBadge(type)}
                                      </div>
                                      {type.description && (
                                        <span
                                          className="report-type-description"
                                          style={{ display: 'block', wordWrap: 'break-word', whiteSpace: 'normal' }}
                                        >
                                          {type.description}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                  {!(isCapacitor && !isGoldOrEnterprise) && (
                                    <HoverTooltip
                                      text={!isGoldOrEnterprise ? goldGatingMsg : (customInstructions[type.name] ? 'Modifier les instructions personnalisées' : 'Ajouter des instructions personnalisées')}
                                      position="left"
                                      arrow={true}
                                      className="instruction-tooltip"
                                    >
                                      <button
                                        className={`report-type-instruction ${customInstructions[type.name] ? 'has-instruction' : ''}`}
                                        disabled={!isGoldOrEnterprise}
                                        onClick={(e) => {
                                          if (!isGoldOrEnterprise) return;
                                          e.stopPropagation();
                                          setCurrentEditingType(type.name);
                                          setInstructionValue(customInstructions[type.name]?.content || '');
                                          setShowInstructionEditor(true);
                                        }}
                                      >
                                        <FaPencilAlt />
                                      </button>
                                    </HoverTooltip>
                                  )}
                                  {canDelete && (
                                    <Button
                                      variant="danger"
                                      type="inversed"
                                      icon={FaTrash}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteType(type.name);
                                      }}
                                      title="Supprimer"
                                    >
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Enterprise Types Section */}
                    {filteredEnterpriseTypes.length > 0 && (
                      <div className="specialty-section enterprise-types">
                        <button
                          className="specialty-header"
                          onClick={() => toggleSpecialty('__enterprise__')}
                        >
                          <div className="specialty-header-content">
                            <span className="specialty-name">
                              Modèles de l'organisation
                              <span className="enterprise-badge">Organisation</span>
                            </span>
                            <span className="specialty-count">
                              {filteredEnterpriseTypes.length} modèle{filteredEnterpriseTypes.length > 1 ? 's' : ''}
                            </span>
                          </div>
                          {expandedSpecialties['__enterprise__'] ? <FaChevronUp /> : <FaChevronDown />}
                        </button>

                        {expandedSpecialties['__enterprise__'] && (
                          <div className="specialty-types">
                            {filteredEnterpriseTypes.map((type) => (
                              <div
                                key={type.name}
                                className="report-type-item"
                                onClick={(e) => {
                                  if (!e.target.closest('.report-type-instruction, .report-type-favorite-btn, .btn')) {
                                    handleToggleFavorite(type);
                                  }
                                }}
                              >
                                <button className="report-type-button">
                                  <span className={`report-type-icon ${ReportTypeNames.getIconClass(type.name)}`}>
                                    {ReportTypeNames.getIconLetter(type.name)}
                                  </span>
                                  <div className="report-type-text">
                                    <div className="report-type-header">
                                      <span className="report-type-name">
                                        {ReportTypeNames.formatDisplayName(type.name)}
                                      </span>
                                      {renderCategoryBadge(type)}
                                    </div>
                                    {type.description && (
                                      <span
                                        className="report-type-description"
                                        style={{ display: 'block', wordWrap: 'break-word', whiteSpace: 'normal' }}
                                      >
                                        {type.description}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Regular Specialty Sections */}
              {getOrderedSpecialties().map((specialty) => {
                const types = specialties[specialty] || [];
                // Exclude favorites from the available types list to prevent duplicates
                const nonFavoriteTypes = types.filter(type => !favorites.includes(type.name));
                const filteredTypes = filterTypesBySearch(nonFavoriteTypes);
                const isUserSpecialty = specialty === userSpecialty;
                const isExpanded = expandedSpecialties[specialty];

                // Hide specialty sections with no results when searching
                if (searchTerm.trim() && filteredTypes.length === 0) {
                  return null;
                }

                return (
                  <div
                    key={specialty}
                    className={`specialty-section ${isUserSpecialty ? 'user-specialty' : ''}`}
                  >
                    <button
                      className="specialty-header"
                      onClick={() => toggleSpecialty(specialty)}
                    >
                      <div className="specialty-header-content">
                        <span className="specialty-name">
                          {ReportTypeNames.formatDisplayName(specialty)}
                          {isUserSpecialty && <span className="user-badge">Votre spécialité</span>}
                        </span>
                        <span className="specialty-count">
                          {filteredTypes.length} modèle{filteredTypes.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>

                    {isExpanded && (
                      <div className="specialty-types">
                        {filteredTypes.map((type) => {
                          const canDelete = reportTypeModel && isPersonalType(type.name);
                          return (
                            <div
                              key={type.name}
                              className="report-type-item"
                              onClick={(e) => {
                                if (!e.target.closest('.report-type-instruction, .report-type-favorite-btn, .btn')) {
                                  handleToggleFavorite(type);
                                }
                              }}
                            >
                              <button className="report-type-button">
                                <span className={`report-type-icon ${ReportTypeNames.getIconClass(type.name)}`}>
                                  {ReportTypeNames.getIconLetter(type.name)}
                                </span>
                                <div className="report-type-text">
                                  <div className="report-type-header">
                                    <span className="report-type-name">
                                      {ReportTypeNames.formatDisplayName(type.name)}
                                    </span>
                                    {renderCategoryBadge(type)}
                                  </div>
                                  {type.description && (
                                    <span
                                      className="report-type-description"
                                      style={{ display: 'block', wordWrap: 'break-word', whiteSpace: 'normal' }}
                                    >
                                      {type.description}
                                    </span>
                                  )}
                                </div>
                              </button>
                              {!(reportTypeModel && reportTypeModel.isEnterprise(type.name)) && !(isCapacitor && !isGoldOrEnterprise) && (
                                <HoverTooltip
                                  text={!isGoldOrEnterprise ? goldGatingMsg : (customInstructions[type.name] ? 'Modifier les instructions personnalisées' : 'Ajouter des instructions personnalisées')}
                                  position="left"
                                  arrow={true}
                                  className="instruction-tooltip"
                                >
                                  <button
                                    className={`report-type-instruction ${customInstructions[type.name] ? 'has-instruction' : ''}`}
                                    disabled={!isGoldOrEnterprise}
                                    onClick={(e) => {
                                      if (!isGoldOrEnterprise) return;
                                      e.stopPropagation();
                                      setCurrentEditingType(type.name);
                                      setInstructionValue(customInstructions[type.name]?.content || '');
                                      setShowInstructionEditor(true);
                                    }}
                                  >
                                    <FaPencilAlt />
                                  </button>
                                </HoverTooltip>
                              )}
                              {canDelete && (
                                <Button
                                  variant="danger"
                                  type="inversed"
                                  icon={FaTrash}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteType(type.name);
                                  }}
                                  title="Supprimer"
                                >
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <Card className="empty-state">
              <p>Aucune spécialité disponible</p>
            </Card>
          )}
        </div>
      </div>
      
      {/* Full Screen Text Editor for Instructions */}
      <FullScreenTextEditor
        isOpen={showInstructionEditor}
        onClose={() => {
          setShowInstructionEditor(false);
          setCurrentEditingType(null);
          setInstructionValue('');
        }}
        onSave={saveInstruction}
        initialValue={instructionValue}
        title={currentEditingType ? `Instructions pour ${ReportTypeNames.formatDisplayName(currentEditingType)}` : 'Instructions personnalisées'}
        placeholder="Entrez vos instructions personnalisées ici. Par exemple : 'Toujours commencer le rapport par...', 'Inclure systématiquement...', 'Format préféré : ...'"
        loading={savingInstruction}
        saveButtonText="Enregistrer les instructions"
      />

      {/* Create Report Type Overlay */}
      {isCreating && (
        <ReportTypeCreatorOverlay
          isOpen={isCreating}
          onClose={cancelCreate}
          existingNames={reportTypeModel?.getNames?.() || []}
          onCreated={async () => {
            await loadData();
            cancelCreate();
          }}
        />
      )}
    </div>
  );
}

export default ReportTypePanel;